import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs';
import { ObraSocial } from 'src/app/models/ObraSocial';
import { UsuarioService } from 'src/app/services/usuario.service'; 
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-obras-sociales',
    templateUrl: './obras-sociales.component.html',
    styleUrls: ['./obras-sociales.component.css'],
    standalone: false
})
export class ObrasSocialesComponent implements OnInit {
  private _authService = inject(AuthService); 
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  
  obrasSociales: ObraSocial[] = [];
  obrasSocialesFiltradas$?: Observable<ObraSocial[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  private OnDestroy$ = new Subject();

  constructor(){
  
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession();
        // 2) Datos
        this.obrasSociales = this._usuarioService.obrasSociales;
        this.filterList?.();
      });
    } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this.router.navigate(['/sign-in']);
    }
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.obrasSocialesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const q = normalizeText(term);

        return (this.obrasSociales ?? [])
          .filter(obraSocial =>
            normalizeText(obraSocial.x_descripcion).includes(q)
          )
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(obraSocial: ObraSocial, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!obraSocial.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    obraSocial.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updateObraSocial(obraSocial));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      obraSocial.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  back(){
    this._location.back();
  }
}

